import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import { RefreshCwIcon } from "lucide-react";
import { useEffect, useState } from "react";

import { useRegistrationList } from "@/lib/queries/use-registration";
import AdminNav from "@/pages/admin/admin-nav";
import RegistrationModal from "@/pages/admin/registration-modal";

export default function AdminRegistrations() {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selected, setSelected] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  const { data, isLoading, refetch, isFetching } = useRegistrationList({
    page,
    limit: 50,
    search: searchQuery,
  });

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 50, total: 0, totalPages: 0 };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [q]);

  const goToPage = (newPage: number) => {
    setPage(newPage);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;

    if (pagination.totalPages <= maxVisible) {
      for (let i = 1; i <= pagination.totalPages; i++) {
        pages.push(
          <PaginationItem key={i}>
            <PaginationLink
              href="#"
              isActive={pagination.page === i}
              onClick={(e) => {
                e.preventDefault();
                goToPage(i);
              }}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }
    } else {
      pages.push(
        <PaginationItem key={1}>
          <PaginationLink
            href="#"
            isActive={pagination.page === 1}
            onClick={(e) => {
              e.preventDefault();
              goToPage(1);
            }}
          >
            1
          </PaginationLink>
        </PaginationItem>
      );

      if (pagination.page > 3) {
        pages.push(
          <PaginationItem key="ellipsis-1">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      const start = Math.max(2, pagination.page - 1);
      const end = Math.min(pagination.totalPages - 1, pagination.page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(
          <PaginationItem key={i}>
            <PaginationLink
              href="#"
              isActive={pagination.page === i}
              onClick={(e) => {
                e.preventDefault();
                goToPage(i);
              }}
            >
              {i}
            </PaginationLink>
          </PaginationItem>
        );
      }

      if (pagination.page < pagination.totalPages - 2) {
        pages.push(
          <PaginationItem key="ellipsis-2">
            <PaginationEllipsis />
          </PaginationItem>
        );
      }

      pages.push(
        <PaginationItem key={pagination.totalPages}>
          <PaginationLink
            href="#"
            isActive={pagination.page === pagination.totalPages}
            onClick={(e) => {
              e.preventDefault();
              goToPage(pagination.totalPages);
            }}
          >
            {pagination.totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    return pages;
  };

  return (
    <div>
      <AdminNav />
      <div className="p-6 space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="space-y-1">
              <CardTitle>Registrations</CardTitle>
              <p className="text-sm text-muted-foreground">
                {pagination.total} total registration{pagination.total !== 1 ? "s" : ""}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-full max-w-xs">
                <Input
                  placeholder="Search by name, email, or phone"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
              <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
                <RefreshCwIcon className={`size-4 ${isFetching ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Surname</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead>Interests</TableHead>
                    <TableHead>Other</TableHead>
                    <TableHead>Banks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center">
                        {searchQuery ? "No results found" : "No data"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((it) => (
                      <TableRow
                        key={it.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => {
                          setSelected(it.id);
                          setOpen(true);
                        }}
                      >
                        <TableCell>{it.id}</TableCell>
                        <TableCell>{new Date(it.created_at).toLocaleString()}</TableCell>
                        <TableCell>{it.name}</TableCell>
                        <TableCell>{it.surname}</TableCell>
                        <TableCell>{it.phone}</TableCell>
                        <TableCell>{it.email}</TableCell>
                        <TableCell>{it.age ?? "-"}</TableCell>
                        <TableCell>{it.interests.join(", ")}</TableCell>
                        <TableCell>{it.interest_other ?? "-"}</TableCell>
                        <TableCell>{it.banks.join(", ")}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            {pagination.totalPages > 1 && (
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (pagination.page > 1) goToPage(pagination.page - 1);
                      }}
                      className={pagination.page === 1 ? "pointer-events-none opacity-50" : ""}
                    />
                  </PaginationItem>
                  {renderPageNumbers()}
                  <PaginationItem>
                    <PaginationNext
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (pagination.page < pagination.totalPages) goToPage(pagination.page + 1);
                      }}
                      className={
                        pagination.page === pagination.totalPages
                          ? "pointer-events-none opacity-50"
                          : ""
                      }
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            )}
          </CardContent>
        </Card>
        <RegistrationModal open={open} onOpenChange={setOpen} itemId={selected} />
      </div>
    </div>
  );
}
